import React, { useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import FAQs from "../components/FAQ/FAQs";
import CardSlider2 from "../components/Slider/CardSlider2";
import CardContainer from "../components/Card/CardContainer";
import Card from "../components/Card/Card";
import Testimonials from "../components/Testimonials/Testimonials";
import Page from "../layout/Page";

const faqData = [
  {
    question:
      "What is Shoora's RoadSense, and how does it enhance road safety?",
    answer:
      "Shoora's RoadSense is an AI-driven Dash Cam that detects potential collision risks in real-time and provides instant video retrieval for accident analysis. It features ADAS technology, including Lane Departure Warning, Forward Collision Warning, and Pedestrian Detection, to prevent accidents and enhance driver safety.",
  },
  {
    question:
      "How does RoadSense protect fleets against false accident claims?",
    answer:
      "RoadSense provides instant video retrieval, allowing fleet managers to access and download HD footage of accidents or incidents. This evidence helps protect against false claims, simplifies the process of determining fault, and minimizes legal costs.",
  },
  {
    question: "What driver training and support features does RoadSense offer?",
    answer:
      "RoadSense provides automated voice alerts and continuous monitoring of proximity, distractions, and driving patterns. The system trains drivers on-the-go, reinforcing safe driving practices through actionable feedback. Drivers also receive immediate assistance from Shoora's control room during critical moments.",
  },
  {
    question: "Can RoadSense integrate with existing fleet management systems?",
    answer:
      "Yes, RoadSense can integrate with existing fleet management systems, providing a seamless and comprehensive solution for fleet operators. Please contact us for more information on integration capabilities.",
  },
  {
    question:
      "How does RoadSense benefit fleet operators in terms of operational efficiency and cost savings?",
    answer:
      "RoadSense benefits fleet operators by reducing accidents, minimizing legal costs, and streamlining insurance procedures. By promoting safer driving habits and providing instant video retrieval, RoadSense helps fleet operators enhance operational efficiency, reduce costs, and improve their overall bottom line.",
  },
  {
    question: "What are some key benefits of RoadSense for fleet operators?",
    answer:
      "Some key benefits of RoadSense for fleet operators include reducing accidents, minimizing legal costs, streamlining insurance procedures, and promoting safer driving habits. By providing instant video retrieval and actionable feedback, RoadSense helps fleet operators enhance operational efficiency, reduce costs, and improve their overall bottom line.",
  },
];

const marutFeatures = [
  {
    id: 1,
    category: "Feature",
    title: "Safety Leadership",
    description:
      "Proactively identify risks, reward excellence, and prioritize safety initiatives.",
    image:
      "/images/features/19.png",
    // link: "#",
    // linkText: "Read more",
  },
  {
    id: 2,
    category: "Feature",
    title: "Fleet Analytics",
    description:
      "Optimize routes, enhance driver performance, and reduce operational costs.",
    image:
      "/images/features/20.png",
    // link: "#",
    // linkText: "Read more",
  },
  {
    id: 3,
    category: "Feature",
    title: "Comprehensive Integration",
    description:
      "Access real-time dashboards, custom reports, and scalable solutions.",
    image:
      "/images/features/21.png",
    // link: "#",
    // linkText: "Read more",
  },
];

const MarutInsights = () => {
  // Function to handle smooth scrolling
  const handleScroll = (id) => {
    const section = document.getElementById(id);
    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  useEffect(()=> {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  },[]);
  return (
    <Page 
    title= "Marut Insights: Data-Driven Fleet Management for Safety, Efficiency, and Cost Optimization"
    description= "Discover Marut Insights, the AI-powered fleet management solution designed to optimize safety, improve operational efficiency, and drive long-term cost savings. Get started today to enhance your fleet's performance."
    keywords= "fleet management, operational efficiency, safety leadership, fleet analytics, cost management, fleet optimization, driver training, AI-powered fleet solutions, Marut Insights, fleet safety"
    >
      {/* Hero Section */}
      <div className="about-hero w-100">
        <img
          src="/images/hero/marut-insights-2.webp"
          alt="Marut Insights Hero"
          className="w-100"
          style={{ objectFit: "cover" }}
        />
        <div
          className="row position-absolute p-5 w-100"
          style={{ top: "80px" }}
        >
          <div className="col col-12 col-lg-6 pe-lg-5">
            <p className="fs-6 text-white mt-5 mb-0">Transform Fleet Operations with</p>
            <h1 className="text-accent fw-bold mb-4 display-4">
              MARUT <i className="fw-light text-light">INSIGHTS</i>
            </h1>
            <p className="fs-6 text-light mb-5 pe-5">
              Empower your fleet with data-driven safety, efficiency, and cost
              optimization. Stand out as a safety leader while safeguarding your
              bottom line.
            </p>
            <div className="d-flex pb-5">
              <button className="btn btn-light rounded-5">
                Get Started Today
              </button>
              <a
                className="ms-3 btn btn-link text-light fw-bold text-decoration-none"
                href="#cta"
              >
                Get a Demo
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Bar */}
      <div className="container-fluid bg-black sticky-top">
        <div className="container nav-links">
          <ul className="navigation-group">
            <li className="navigation-item">
              <button
                onClick={() => handleScroll("overview")}
                className="navigation-item-link btn btn-link text-white"
              >
                Overview
              </button>
            </li>
            <li className="navigation-item">
              <button
                onClick={() => handleScroll("features")}
                className="navigation-item-link btn btn-link text-white"
              >
                Features
              </button>
            </li>
            <li className="navigation-item">
              <button
                onClick={() => handleScroll("benefits")}
                className="navigation-item-link btn btn-link text-white"
              >
                Benefits
              </button>
            </li>
            <li className="navigation-item">
              <button
                onClick={() => handleScroll("testimonials")}
                className="navigation-item-link btn btn-link text-white"
              >
                Testimonials
              </button>
            </li>
            <li className="navigation-item">
              <button
                onClick={() => handleScroll("faqs")}
                className="navigation-item-link btn btn-link text-white"
              >
                FAQs
              </button>
            </li>
          </ul>
        </div>
      </div>

      {/* Add IDs to the Sections */}
      <section id="overview" className="py-5">
        <div className="container">
          <h2 className="text-center fw-bold mb-4">What is Marut Insights?</h2>
          <p className="text-center mb-5">
            Stand out as a safety leader. Use powerful insights and predictive
            decision-making to prioritize safety initiatives, attract top
            talent, recognize excellence, and safeguard your bottom line.
            <br />
            <br />A revolutionary approach to analyzing and optimizing fleet
            operations. Marut Insights offers a comprehensive data package that
            enables smarter route planning, accurate ETAs, driver optimization,
            and cost reduction—all powered by analytics designed to enhance
            fleet efficiency.
          </p>
        </div>
      </section>

      {/* Benefits Section */}

      


      <section id="features" className="py-5">
        <CardSlider2 data={marutFeatures} />
      </section>

      <section id="benefits" className="py-5">
        <CardContainer heading="Benefits" desc="Why Choose Marut Insights?">
          <Card
            // category="Safety"
            bgColor="#f4f4f4"
            type="primary"
            headingColor="#0d004a"
            heading="Operational Efficiency"
            desc="Streamline processes, reduce downtime, and maximize fleet utilization."
            imgsrc="/images/benefits/18.png"
            // linkText="See Plans & Pricing"
          />
          <Card
            // category="Visibility"
            bgColor="#eaca13"
            type="vibrant"
            heading="Safety & Accountability"
            desc="Lower accidents and foster a culture of responsibility with actionable insights."
            imgsrc="/images/benefits/19.png"
          />
          <Card
            // category="Coaching insights"
            type="primary"
            heading="Cost Management"
            desc="Identify inefficiencies, reduce expenses, and optimize resource allocation."
            imgsrc="/images/benefits/20.png"
            // linkText="See Plans & Pricing"
          />
        </CardContainer>
      </section>

      {/* ROI Section */}
      <section
        className="py-5"
        id="cta"
        style={{ backgroundColor: "var(--accent-color)" }}
      >
        <div className="container text-center">
          <h2 className="fw-bold mb-4 display-1">Drive Long-Term ROI</h2>
          <p className="mb-4 px-5 fs-2">
            Marut Insights delivers measurable results by reducing accidents,
            optimizing operations, and building a strong reputation for safety
            and efficiency.
          </p>
          <a href="/demo" className="btn btn-light btn-lg fw-bold rounded-5">
            Schedule a Demo
          </a>
        </div>
      </section>

      <section id="testimonials" className="py-5">
        <div className="testimonials-section section">
          <div className="container">
          <Testimonials/>
          </div>
        </div>
      </section>

      <section id="faqs" className="py-5">
        <FAQs
          data={faqData}
          title="Your questions about MarutInsights, answered."
        />
      </section>
    </Page>
  );
};

export default MarutInsights;
